import * as THREE from 'three';
import { Group, Vector3 } from 'three';
import TWEEN from '@tweenjs/tween.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { ELementType } from '@blade-sxp/map/src/element';
import { FPS } from '../config';
class Person extends Group {
    props;
    baseActions;
    additiveActions;
    currentBaseAction;
    allActions;
    mixer;
    skeleton;
    isLoaded;
    currentVector3;
    isObserver;
    disposeStatus = false;
    static gltf;
    static model;
    static animations;
    static circle = new THREE.Mesh(new THREE.CircleGeometry(0.4, 32), new THREE.MeshBasicMaterial({
        side: THREE.DoubleSide,
        transparent: true,
        depthTest: true,
    }));
    model;
    circle;
    animateId = -1;
    tween;
    animate; // 动画
    constructor(data) {
        super();
        // const mesh = new THREE.Mesh(new THREE.PlaneGeometry(100, 100), new THREE.MeshPhongMaterial({ color: 0x999999, depthWrite: false }));
        // mesh.rotation.x = -Math.PI / 2;
        // mesh.receiveShadow = true;
        // this.add(mesh);
        this.props = data;
        const platformName = `${ELementType.Roi3d}${this.props.renderProps.platformId}`;
        const position = this.props.getPointInMesh(platformName, { x: this.props.position.x, y: this.props.position.y });
        this.position.set(position.x, position.y, position.z);
        this.name = this.uuid;
        // this.position.set(10, 10, 50);
        const loader = new GLTFLoader();
        this.allActions = [];
        this.isObserver = false;
        this.isLoaded = false;
        this.tween = new TWEEN.Tween({
            x1: this.position.x,
            y1: this.position.y,
            z1: this.position.z,
        });
        this.currentVector3 = new Vector3(0, 0, 1);
        this.currentBaseAction = 'stand';
        this.additiveActions = {
            sneak_pose: { weight: 0 },
            sad_pose: { weight: 0 },
            agree: { weight: 0 },
            headShake: { weight: 0 },
        };
        this.baseActions = {
            stand: { weight: 1 },
            walk: { weight: 0 },
            run: { weight: 0 },
        };
        // https://threejs.org/examples/models/gltf/Xbot.glb
        loader.load('/human.glb', (gltf) => {
            Person.model = gltf.scene;
            Person.animations = gltf.animations;
            this.circle = Person.circle.clone();
            this.circle.material = Person.circle.material.clone();
            this.circle.material.color = new THREE.Color(this.props.renderProps.signalColor).convertSRGBToLinear();
            this.circle.material.opacity = this.props.renderProps.signalColor ? 1 : 0;
            this.circle.material.transparent = true;
            this.circle.material.depthTest = false;
            this.circle.position.set(Person.model.position.x, Person.model.position.y - 0.1, Person.model.position.z);
            this.circle.rotation.x = Math.PI * 0.5;
            this.model = Person.model;
            this.model.scale.set(100, 100, 100);
            // 用来模拟骨骼 Skeleton 的辅助对象. 该辅助对象使用 LineBasicMaterial 材质.
            // this.skeleton = new THREE.SkeletonHelper(model);
            // this.skeleton.visible = false;
            // this.add(this.skeleton);
            // 阴影相关
            this.model.traverse((object) => {
                if (object.isMesh) {
                    // object.castShadow = true;
                    // object.material.translate = true;
                    object.material.color.set('black');
                    object.material.transparent = true;
                    object.material.depthTest = false;
                    object.material.emissive.set('black');
                    object.material.opacity = 0;
                }
            });
            const tween = new TWEEN.Tween({
                opacity: 0,
            })
                .to({
                opacity: this.circle.material.opacity,
            }, 1000)
                .onUpdate((obj) => {
                if (this.disposeStatus) {
                    tween.stop();
                    return;
                }
                if (this.circle) {
                    this.circle.material.opacity = obj.opacity;
                }
                if (this.model) {
                    this.model.traverse((object) => {
                        if (object.isMesh) {
                            object.material.opacity = obj.opacity;
                        }
                    });
                }
            })
                .start();
            this.add(this.model, this.circle);
            const animations = Person.animations;
            this.mixer = new THREE.AnimationMixer(this.model);
            animations.map((animation) => {
                const name = animation.name;
                if (this.baseActions[name]) {
                    const action = this.mixer.clipAction(animation);
                    this.activateAction(action);
                    this.baseActions[name].action = action;
                    this.allActions.push(action);
                }
                else if (this.additiveActions[name]) {
                    THREE.AnimationUtils.makeClipAdditive(animation);
                    if (animation.name.endsWith('_pose')) {
                        animation = THREE.AnimationUtils.subclip(animation, animation.name, 2, 3, 30);
                    }
                    const action = this.mixer.clipAction(animation);
                    this.activateAction(action);
                    this.additiveActions[name].action = action;
                    this.allActions.push(action);
                }
            });
            this.isLoaded = true;
        });
        let timeNow = 0;
        const renderT = 1 / FPS;
        const clock = new THREE.Clock();
        this.animate = () => {
            this.animateId = window.requestAnimationFrame(this.animate);
            const mixerUpdateDelta = clock.getDelta();
            timeNow = timeNow + mixerUpdateDelta;
            if (timeNow > renderT) {
                this.mixer?.update(mixerUpdateDelta);
                timeNow = 0;
            }
        };
        this.animate();
    }
    update(data) {
        const platformName = `${ELementType.Roi3d}${data.renderProps.platformId}`;
        const position = this.props.getPointInMesh(platformName, { x: data.position.x, y: data.position.y });
        if (!position)
            return;
        if (this.circle && this.circle.material) {
            this.circle.material.color = new THREE.Color(data?.renderProps?.signalColor).convertSRGBToLinear();
            this.circle.material.opacity = data?.renderProps?.signalColor ? 1 : 0;
        }
        if (!this.isLoaded) {
            this.position.set(position.x, position.y, position.z);
            this.props = data;
        }
        else {
            this.tween
                .stop()
                .to({
                x1: position.x,
                y1: position.y,
                z1: position.z,
            }, 10000)
                .onUpdate((obj) => {
                if (this.disposeStatus) {
                    this.tween.stop();
                    return;
                }
                this.position.set(obj.x1, obj.y1, obj.z1);
            })
                .onStop(() => {
                if (this.disposeStatus)
                    return;
                this.changeBaseAction('stand');
                this.allActions.map((action) => {
                    const clip = action.getClip();
                    const settings = this.baseActions[clip.name] || this.additiveActions[clip.name];
                    settings.weight = action.getEffectiveWeight();
                });
            })
                .onComplete(() => {
                if (this.disposeStatus)
                    return;
                this.changeBaseAction('stand');
                this.allActions.map((action) => {
                    const clip = action.getClip();
                    const settings = this.baseActions[clip.name] || this.additiveActions[clip.name];
                    settings.weight = action.getEffectiveWeight();
                });
                this.props = data;
            })
                .onStart(() => {
                this.changeBaseAction('walk');
                this.allActions.map((action) => {
                    const clip = action.getClip();
                    const settings = this.baseActions[clip.name] || this.additiveActions[clip.name];
                    settings.weight = action.getEffectiveWeight();
                });
                const _position = new THREE.Vector3(position.x, this.position.y, position.z).sub(this.position.clone());
                if (_position.length() !== 0) {
                    const normalVector = new THREE.Vector3();
                    normalVector.crossVectors(this.currentVector3, _position); // 坐标平面法线
                    const isReverse = normalVector.y > 0;
                    const angle = this.currentVector3.angleTo(_position);
                    this.currentVector3 = _position;
                    // this.rotation.y = 10;
                    this.rotateY(isReverse ? angle : -angle);
                }
            })
                .start();
        }
    }
    changeBaseAction(name) {
        const settings = this.baseActions[name];
        const currentSettings = this.baseActions[this.currentBaseAction];
        const currentAction = currentSettings ? currentSettings.action : null;
        const action = settings ? settings.action : null;
        if (currentAction !== action) {
            this.prepareCrossFade(currentAction, action, 0.35);
        }
    }
    prepareCrossFade(startAction, endAction, duration) {
        // If the current action is 'stand', execute the crossfade immediately;
        // else wait until the current action has finished its current loop
        // if (this.currentBaseAction === 'stand' || !startAction || !endAction) {
        //   this.executeCrossFade(startAction, endAction, duration);
        // } else {
        //   this.synchronizeCrossFade(startAction, endAction, duration);
        // }
        this.executeCrossFade(startAction, endAction, duration);
        // Update control colors
        if (endAction) {
            const clip = endAction.getClip();
            this.currentBaseAction = clip.name;
        }
        else {
            this.currentBaseAction = 'None';
        }
    }
    executeCrossFade(startAction, endAction, duration) {
        if (endAction) {
            this.setWeight(endAction, 1);
            endAction.time = 0;
            if (startAction) {
                // Crossfade with warping
                startAction.crossFadeTo(endAction, duration, true);
            }
            else {
                // Fade in
                endAction.fadeIn(duration);
            }
        }
        else {
            // Fade out
            startAction.fadeOut(duration);
        }
    }
    synchronizeCrossFade(startAction, endAction, duration) {
        const that = this;
        function onLoopFinished(event) {
            if (event.action === startAction) {
                that.mixer?.removeEventListener('loop', onLoopFinished);
                that.executeCrossFade(startAction, endAction, duration);
            }
        }
        this.mixer?.addEventListener('loop', onLoopFinished);
    }
    activateAction(action) {
        const clip = action.getClip();
        const settings = this.baseActions[clip.name] || this.additiveActions[clip.name];
        this.setWeight(action, settings.weight);
        action.play();
    }
    // This function is needed, since animationAction.crossFadeTo() disables its start action and sets
    // the start action's timeScale to ((start animation's duration) / (end animation's duration))
    setWeight(action, weight) {
        action.enabled = true;
        action.setEffectiveTimeScale(1);
        action.setEffectiveWeight(weight);
    }
    dispose() {
        const tween = new TWEEN.Tween({
            opacity: 1,
        })
            .to({
            opacity: 0,
        }, 1000)
            .onUpdate((obj) => {
            if (this.disposeStatus) {
                tween.stop();
                return;
            }
            if (this.circle) {
                this.circle.material.opacity = obj.opacity;
            }
            if (this.model) {
                this.model.traverse((object) => {
                    if (object.isMesh) {
                        object.material.opacity = obj.opacity;
                    }
                });
            }
        })
            .onComplete(() => {
            const { onDestory } = this.props;
            window.cancelAnimationFrame(this.animateId);
            this.disposeStatus = true;
            this.model = undefined;
            this.circle = undefined;
            this.traverse(function (obj) {
                if (obj instanceof THREE.Mesh) {
                    obj.geometry.dispose();
                    obj.material.dispose();
                }
                else {
                    if (obj.parent) {
                        obj?.parent.remove(obj);
                    }
                    obj.clear();
                }
            });
            this.parent?.remove(this);
            this.clear();
            if (onDestory && typeof onDestory === 'function') {
                onDestory(this);
            }
        })
            .start();
    }
}
export default Person;
